import { useCallback, useEffect, useMemo, useState } from 'react';
import { sessionsService } from '../../../../services/sessions/sessions.service';

type UseAutoCloseSessionControllerParams = {
  opened: boolean;
  tenantId: string;
  employeeSession: string | null;
  autoCloseTime: string | null;
  sessionOpenTime: string | null;
  onSaved: (next: string | null) => void;
  onClose: () => void;
};

const pad = (n: number): string => String(n).padStart(2, '0');

const ISRAEL_TZ = 'Asia/Jerusalem';

// Asia/Jerusalem wall-clock parts of an instant (feature's canonical tz, not the device's).
function israelParts(date: Date): { year: number; month: number; day: number; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: ISRAEL_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}

const label = (year: number, month: number, day: number, hour: number, minute: number): string =>
  `${pad(day)}-${pad(month)}-${String(year).slice(2)} ${pad(hour)}:${pad(minute)}`;

// Next Israel fire datetime for an "HH:MM": today if still ahead in Israel time, else tomorrow.
function computeNextFireLabel(time: string | null): string {
  if (!time) {
    return '--';
  }
  const [h, m] = time.split(':').map(Number);
  const now = israelParts(new Date());
  let dateMs = Date.UTC(now.year, now.month - 1, now.day);
  if (h * 60 + m <= now.hour * 60 + now.minute) {
    dateMs += 24 * 60 * 60 * 1000;
  }
  const d = new Date(dateMs);
  return label(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), h, m);
}

// Format an ISO instant as its Asia/Jerusalem wall-clock "DD-MM-YY HH:MM".
function formatIsrael(iso: string | null): string {
  if (!iso) {
    return '--';
  }
  const p = israelParts(new Date(iso));
  return label(p.year, p.month, p.day, p.hour, p.minute);
}

// State + diff-only save for the auto-close-time modal (baseline snapshot taken on open, Rule 19).
export function useAutoCloseSessionController({
  opened,
  tenantId,
  employeeSession,
  autoCloseTime,
  sessionOpenTime,
  onSaved,
  onClose,
}: UseAutoCloseSessionControllerParams) {
  const [enabled, setEnabled] = useState<boolean>(autoCloseTime !== null);
  const [time, setTime] = useState<string>(autoCloseTime ?? '05:00');
  const [baseline, setBaseline] = useState<string | null>(autoCloseTime);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setEnabled(autoCloseTime !== null);
      setTime(autoCloseTime ?? '05:00');
      setBaseline(autoCloseTime);
      setError(null);
    }
  }, [opened, autoCloseTime]);

  const nextValue = enabled ? time : null;
  const isDirty = nextValue !== baseline;

  const nextFire = useMemo(() => computeNextFireLabel(nextValue), [nextValue]);
  const openSince = useMemo(() => formatIsrael(sessionOpenTime), [sessionOpenTime]);

  const handleSave = useCallback(async () => {
    if (!employeeSession) {
      return;
    }
    if (!isDirty) {
      onClose();
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await sessionsService.updateAutoCloseTime(tenantId, nextValue, employeeSession);
      onSaved(nextValue);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error && err.message ? err.message : 'שמירת שעת הסגירה נכשלה');
    } finally {
      setIsSaving(false);
    }
  }, [employeeSession, isDirty, nextValue, onClose, onSaved, tenantId]);

  return { enabled, setEnabled, time, setTime, isDirty, isSaving, error, nextFire, openSince, handleSave };
}
